import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { InstantGameSummary } from "@/lib/instant-games.functions";

export function InstantGameCard({
  game,
  stateSlug,
}: {
  game: InstantGameSummary;
  stateSlug: string;
}) {
  return (
    <div className="bg-card rounded-xl overflow-hidden shadow-sm border border-border flex flex-col transition hover:shadow-md hover:border-primary/40">
      <Link
        to="/states/$stateSlug/instant/$gameSlug"
        params={{ stateSlug, gameSlug: game.slug }}
        className="relative block aspect-[4/3] overflow-hidden group"
        aria-label={`View ${game.name} prizes, odds and details`}
      >
        {game.image_url ? (
          <img
            src={game.image_url}
            alt={game.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-muted" />
        )}
        <span className="absolute bottom-2 right-2 max-w-[85%] truncate bg-background/95 text-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-md">
          View {game.name} details
        </span>
      </Link>
      <div className="p-3 flex flex-col flex-1">
        <h3
          className="font-display font-bold text-[15px] text-foreground leading-snug line-clamp-1 mb-2"
          title={game.name}
        >
          {game.name}
        </h3>
        <dl className="mb-3 space-y-1">
          {game.top_prize && (
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-xs font-semibold text-foreground">Top Prize:</dt>
              <dd className="text-sm font-bold text-foreground">{game.top_prize}</dd>
            </div>
          )}
          {game.price_range && (
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-xs font-semibold text-foreground">Play Amount:</dt>
              <dd className="text-sm font-bold text-foreground">{game.price_range}</dd>
            </div>
          )}
        </dl>
        {game.affiliate_link ? (
          <Button
            asChild
            className="mt-auto w-full h-10 px-6 rounded-full font-bold tracking-wide bg-accent text-white hover:bg-accent/90 hover:text-white"
          >
            <a
              href={game.affiliate_link}
              target="_blank"
              rel="noopener nofollow sponsored"
            >
              PLAY NOW
            </a>
          </Button>
        ) : (
          <Button asChild variant="outline" className="mt-auto w-full h-10 px-6 rounded-full">
            <Link
              to="/states/$stateSlug/instant/$gameSlug"
              params={{ stateSlug, gameSlug: game.slug }}
            >
              View Details <ExternalLink className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}